import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';

import { ModalFrame, ModalFrameEntity, ModalFrameFooter, ModalFrameInner } from 'components/modal';
import ActionButton from 'components/buttons/action-button/ActionButton';
import Spinner from 'components/spinner/Spinner';

import InfluencerCreatorNetworkListContext from './context/InfluencerCreatorNetworkListContext';
import InfluencerCreatorNetworkListItems from './items/InfluencerCreatorNetworkListItems';

import { info } from 'redux-store/reducers/common-reducer';
import { fetchList, resetConnections, resetList } from '../reducers/influencer-creator-network-list';

import { __t, applyTranslations } from 'utils/language';

import './influencer-creator-network-list.scss';

import type {
  InfluencerCreatorNetworkListLoading,
  InfluencerProfile,
  NetworkGraphNodeRaw
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./influencer-creator-network-list.i18n.json'));

type OwnProps = {
  influencerProfile: InfluencerProfile,
  onCloseModal: () => void
};

type StateProps = {
  list: Array<NetworkGraphNodeRaw>,
  connections: Array<NetworkGraphNodeRaw>,
  isLoading: InfluencerCreatorNetworkListLoading
};

type DispatchProps = {
  dispatchFetchList: (b: number, a: boolean) => Promise<any>,
  dispatchResetList: () => void,
  dispatchResetConnections: () => void,
  dispatchNotification: (b: string, a: number) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  expandedId: number | undefined | null, // influencer profile id,
  disabledItems: Record<number, boolean>
};

const MSG_KEY = 'influencer_creator_network_list';

class InfluencerCreatorNetworkList extends React.PureComponent<Props, State> {
  state = {
    expandedId: null,
    disabledItems: {}
  };

  componentDidMount() {
    const { influencerProfile } = this.props;

    this.handleFetch(influencerProfile.id);
  }

  componentWillUnmount() {
    const { dispatchResetList, dispatchResetConnections } = this.props;

    dispatchResetList();
    dispatchResetConnections();
  }

  sortItems(items: Array<NetworkGraphNodeRaw>): Array<NetworkGraphNodeRaw> {
    // $FlowIgnore - Flow thinks `orderBy` only accepts objects. But it also accepts arrays.
    return orderBy(items, ({ followers }) => followers || 0, ['desc']);
  }

  handleFetch = (influencerProfileId: number, isConnections: boolean = false): void => {
    const { dispatchFetchList } = this.props;

    dispatchFetchList(influencerProfileId, isConnections)
      .then(results => {
        if (results && isConnections) {
          const filteredConnections = (results.payload.connections || []).filter(item => item.symbolSize !== 1); // main node

          this.handleDisableItem(influencerProfileId, isEmpty(filteredConnections));
        }
      });
  };

  handleDisableItem(influencerProfileId: number, isDisabled: boolean): void {
    const { dispatchNotification } = this.props;

    if (isDisabled) {
      dispatchNotification(__t(`${MSG_KEY}.messages.no_followers`), 3000);
    }

    this.setState(state => ({
      disabledItems: {
        ...state.disabledItems,
        [influencerProfileId]: isDisabled
      }
    }));
  }

  handleToggleItem = (event: SyntheticEvent<HTMLElement>): void => {
    const { dispatchResetConnections } = this.props;
    const target = event.currentTarget;
    const influencerProfileId = Number(target.dataset.profileId) || null;
    const isExpanded = target.dataset.isExpanded === 'true';

    this.setState({
      expandedId: !isExpanded ? influencerProfileId : null
    });

    if (!isExpanded) {
      dispatchResetConnections();
      this.handleFetch(Number(influencerProfileId), !isExpanded);
    }
  };

  renderSpinner(): React.ReactNode {
    return (
      <div className="influencer-creator-network-list__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderHeader(): React.ReactNode {
    const { influencerProfile } = this.props;

    return (
      <div className="influencer-creator-network-list__header">
        <div className="influencer-creator-network-list__header-entity">
          <ModalFrameEntity
            influencerProfile={influencerProfile}
          />
        </div>
        <div className="influencer-creator-network-list__header-text">
          {__t(`${MSG_KEY}.messages.description`)}
        </div>
        <div className="influencer-creator-network-list__header-text">
          {__t(`${MSG_KEY}.messages.how_to`)}
        </div>
      </div>
    );
  }

  renderListItems(): React.ReactNode {
    const { list, connections } = this.props;

    return (
      <div className="influencer-creator-network-list__list">
        <InfluencerCreatorNetworkListItems
          list={this.sortItems(list)}
          connections={this.sortItems(connections)}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const { influencerProfile, isLoading, onCloseModal } = this.props;
    const { expandedId, disabledItems } = this.state;

    const context = {
      rootInfluencerName: influencerProfile.name || influencerProfile.artistName,
      expandedId,
      disabledItems,
      isLoading,
      onToggleItem: this.handleToggleItem
    };

    return (
      <InfluencerCreatorNetworkListContext.Provider value={context}>
        <div className="influencer-creator-network-list">
          <ModalFrame title={__t(`${MSG_KEY}.title`)}>
            {this.renderHeader()}
            <ModalFrameInner>
              {
                isLoading.list
                  ? this.renderSpinner()
                  : this.renderListItems()
              }
            </ModalFrameInner>
            <ModalFrameFooter>
              <div className="influencer-creator-network-list__button">
                <ActionButton
                  onClick={onCloseModal}
                >
                  {__t(`${MSG_KEY}.buttons.close`)}
                </ActionButton>
              </div>
            </ModalFrameFooter>
          </ModalFrame>
        </div>
      </InfluencerCreatorNetworkListContext.Provider>
    );
  }
}

const mapStateToProps = ({ influencerCreatorNetworkList }): StateProps => ({
  list: influencerCreatorNetworkList.list,
  connections: influencerCreatorNetworkList.connections,
  isLoading: influencerCreatorNetworkList.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchList: (influencerProfileId: number, isConnections: boolean) => (
    dispatch(fetchList(influencerProfileId, isConnections))
  ),

  dispatchResetList: () => (
    dispatch(resetList())
  ),

  dispatchResetConnections: () => (
    dispatch(resetConnections())
  ),

  dispatchNotification: (msg: string, hideDuration: number) => (
    dispatch(info(msg, { hideDuration }))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(InfluencerCreatorNetworkList);
